import React, { useMemo } from 'react';
import { Database, ExternalLink, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import { Button } from '@easyflow/ui-master/components/ui/button';

interface ViteEnvMeta {
  env?: { VITE_CLOUDBEAVER_EMBED_URL?: string };
}

function getCloudBeaverUrl(): string {
  const meta = import.meta as unknown as ViteEnvMeta;
  return meta.env?.VITE_CLOUDBEAVER_EMBED_URL ?? 'https://dbstudio.easyflowai.it';
}

export default function CloudBeaverStudio(): JSX.Element {
  const cloudbeaverUrl = useMemo(() => getCloudBeaverUrl(), []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-white flex items-center gap-2">
            <Database className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            DB Studio
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Client DBA grafico (CloudBeaver CE) sull'intero cluster Postgres. Browsing schema, SQL
            editor, data grid editabile, ER diagram. Connessione cross-tenant via{' '}
            <code className="text-xs">{cloudbeaverUrl}</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={cloudbeaverUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5 mr-2" />
              Apri in nuova scheda
            </a>
          </Button>
        </div>
      </div>

      {/* Warning cross-tenant / bypass RLS */}
      <div className="flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-3">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
        <p className="text-xs text-amber-800 dark:text-amber-200 leading-snug">
          Strumento amministrativo: la connessione usa il ruolo <code>efai_dba</code> con{' '}
          <strong>BYPASSRLS</strong> e vede <strong>tutti i tenant / tutti gli schemi</strong>{' '}
          (<code>public.*</code>, <code>master.*</code>). Lettura, scrittura (DML) e DDL abilitati.
          Usare con cautela.
        </p>
      </div>

      {/* CloudBeaver iframe */}
      <Card className="overflow-hidden">
        <CardHeader className="py-3 px-4 flex flex-row items-center gap-2 border-b">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Database className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            CloudBeaver
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <iframe
            title="CloudBeaver DB Studio"
            src={cloudbeaverUrl}
            className="w-full border-0"
            style={{ height: 'calc(100vh - 280px)', minHeight: 640 }}
            allow="clipboard-read; clipboard-write; fullscreen"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads allow-modals"
            referrerPolicy="no-referrer"
          />
          <p className="text-[11px] text-muted-foreground px-4 py-2 border-t">
            Se l'iframe è vuoto: verifica DNS <code>dbstudio.easyflowai.it</code>, TLS Let's Encrypt,
            il container CloudBeaver su OBS-1 (<code>:8978</code>) e l'header{' '}
            <code>Content-Security-Policy: frame-ancestors</code> nel vhost nginx FE-1. Vedi runbook{' '}
            <code>cloudbeaver-dbstudio.md</code>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
